// src/utils/jobApplicationUtils.ts

import { ApplicationStatus, JobApplicationEnhanced } from '../types/jobSearch';

// Define valid status transitions
const validStatusTransitions: Record<ApplicationStatus, ApplicationStatus[]> = {
    saved: ['applied', 'declined'],
    applied: ['screening', 'interview_scheduled', 'denied', 'declined'],
    screening: ['interview_scheduled', 'technical_assessment', 'denied', 'declined'],
    interview_scheduled: ['interview_rounds', 'technical_assessment', 'denied', 'declined'],
    interview_rounds: ['technical_assessment', 'reference_check', 'offered', 'denied', 'declined'],
    technical_assessment: ['interview_rounds', 'reference_check', 'offered', 'denied', 'declined'],
    reference_check: ['offered', 'denied', 'declined'],
    denied: [],
    offered: ['negotiating', 'accepted', 'declined'],
    negotiating: ['accepted', 'declined'],
    accepted: [],
    declined: []
};

// Status display configurations
export const statusConfig: Record<ApplicationStatus, {
    label: string;
    color: string;
    bgColor: string;
    icon: string;
    description: string;
}> = {
    saved: {
        label: 'Saved',
        color: '#9CA3AF',  // gray-400
        bgColor: 'bg-gray-400',
        icon: '💾',
        description: 'Job saved for later application'
    },
    applied: {
        label: 'Applied',
        color: '#3B82F6',  // blue-500
        bgColor: 'bg-blue-500',
        icon: '📤',
        description: 'Application submitted'
    },
    screening: {
        label: 'Screening',
        color: '#8B5CF6',  // purple-500
        bgColor: 'bg-purple-500',
        icon: '📱',
        description: 'Initial screening in progress'
    },
    interview_scheduled: {
        label: 'Interview Scheduled',
        color: '#EAB308',  // yellow-500
        bgColor: 'bg-yellow-500',
        icon: '📅',
        description: 'Upcoming interview'
    },
    interview_rounds: {
        label: 'Interview Rounds',
        color: '#F59E0B',  // amber-500
        bgColor: 'bg-amber-500',
        icon: '👥',
        description: 'Multiple interview rounds'
    },
    technical_assessment: {
        label: 'Technical Assessment',
        color: '#10B981',  // emerald-500
        bgColor: 'bg-emerald-500',
        icon: '💻',
        description: 'Technical assessment or coding challenge'
    },
    reference_check: {
        label: 'Reference Check',
        color: '#6366F1',  // indigo-500
        bgColor: 'bg-indigo-500',
        icon: '📋',
        description: 'Reference check in progress'
    },
    denied: {
        label: 'Denied',
        color: '#EF4444',  // red-500
        bgColor: 'bg-red-500',
        icon: '❌',
        description: 'Application denied'
    },
    offered: {
        label: 'Offered',
        color: '#14B8A6',  // teal-500
        bgColor: 'bg-teal-500',
        icon: '🎉',
        description: 'Job offer received'
    },
    negotiating: {
        label: 'Negotiating',
        color: '#8B5CF6',  // purple-500
        bgColor: 'bg-purple-500',
        icon: '💬',
        description: 'Offer negotiation in progress'
    },
    accepted: {
        label: 'Accepted',
        color: '#22C55E',  // green-500
        bgColor: 'bg-green-500',
        icon: '✅',
        description: 'Job offer accepted'
    },
    declined: {
        label: 'Declined',
        color: '#6B7280',  // gray-500
        bgColor: 'bg-gray-500',
        icon: '🚫',
        description: 'Application declined by candidate'
    }
};

// Utility functions
export const isValidStatusTransition = (
    currentStatus: ApplicationStatus,
    newStatus: ApplicationStatus
): boolean => {
    return validStatusTransitions[currentStatus].includes(newStatus);
};

export const getNextPossibleStatuses = (
    currentStatus: ApplicationStatus
): ApplicationStatus[] => {
    return validStatusTransitions[currentStatus];
};

export const calculateApplicationDuration = (
    application: JobApplicationEnhanced
): number => {
    const startDate = new Date(application.application_date);
    const endDate = application.status === 'accepted' ||
        application.status === 'declined' ||
        application.status === 'denied'
        ? new Date(application.last_status_change)
        : new Date();

    return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
};

export const getStatusDuration = (
    application: JobApplicationEnhanced,
    status: ApplicationStatus
): number => {
    const statusHistory = application.status_history;
    const statusEntry = statusHistory.find(h => h.status === status);

    if (!statusEntry) return 0;

    const startDate = new Date(statusEntry.timestamp);
    const nextStatusEntry = statusHistory.find(
        h => new Date(h.timestamp) > startDate
    );
    const endDate = nextStatusEntry
        ? new Date(nextStatusEntry.timestamp)
        : new Date();

    return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
};

export const groupApplicationsByStatus = (
    applications: JobApplicationEnhanced[]
): Record<ApplicationStatus, JobApplicationEnhanced[]> => {
    return applications.reduce((acc, app) => {
        if (!acc[app.status]) {
            acc[app.status] = [];
        }
        acc[app.status].push(app);
        return acc;
    }, {} as Record<ApplicationStatus, JobApplicationEnhanced[]>);
};

export const calculateApplicationMetrics = (
    applications: JobApplicationEnhanced[]
) => {
    const metrics = {
        totalApplications: applications.length,
        activeApplications: applications.filter(app => app.is_active).length,
        averageTimeToOffer: 0,
        offerRate: 0,
        interviewRate: 0,
        statusDistribution: {} as Record<ApplicationStatus, number>,
    };

    if (applications.length === 0) {
        return metrics;
    }

    const offeredApplications = applications.filter(
        app => app.status === 'offered' || app.status === 'accepted'
    );

    if (offeredApplications.length > 0) {
        metrics.averageTimeToOffer = Math.round(
            offeredApplications.reduce(
                (sum, app) => sum + calculateApplicationDuration(app),
                0
            ) / offeredApplications.length
        );
    }

    metrics.offerRate = applications.length > 0
        ? Number(((offeredApplications.length / applications.length) * 100).toFixed(1))
        : 0;

    const interviewedApplications = applications.filter(
        app => app.interviews && app.interviews.length > 0
    );
    metrics.interviewRate = applications.length > 0
        ? Number(((interviewedApplications.length / applications.length) * 100).toFixed(1))
        : 0;

    // Calculate status distribution
    applications.forEach(app => {
        if (!metrics.statusDistribution[app.status]) {
            metrics.statusDistribution[app.status] = 0;
        }
        metrics.statusDistribution[app.status]++;
    });

    return metrics;
};

export const generateApplicationSummary = (
    application: JobApplicationEnhanced
): string => {
    const duration = calculateApplicationDuration(application);
    const currentStatus = statusConfig[application.status];
    const interviews = application.interviews.length;

    return `${application.company} - ${application.job_title}\n` +
        `Status: ${currentStatus.label} ${currentStatus.icon}\n` +
        `Duration: ${duration} days\n` +
        `Interviews: ${interviews}\n` +
        `Location: ${application.location || 'Not specified'}\n` +
        `Tags: ${application.tags.join(', ') || 'None'}`;
};